import {
  Typography,
  Label,
  TagsInput,
  TagsInputList,
  TagsInputInput,
  TagsInputItem,
  Combobox,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { useCallback, useMemo } from "react";
import { Control, Controller, FieldErrors } from "react-hook-form";
import type { SubscriptionFormData } from "@/pages/subscriptions/services/types";
import StepCard from "../../CompletedStepCard";
import { useSlackChannels } from "@/pages/alerts/useSlackChannels";
import { ChannelType, WizardStepEnum } from "@/pages/subscriptions/constants";
import type { WizardStep } from "../../types";

interface DestinationStepProps {
  control: Control<SubscriptionFormData>;
  errors: FieldErrors<SubscriptionFormData>;
  onGoToStep: (step: WizardStep) => void;
}

const DestinationStep = ({ control, errors, onGoToStep }: DestinationStepProps) => {
  const { slackChannels, isLoading: isLoadingChannels } = useSlackChannels();

  // Transform Slack channels from API to options format
  const slackChannelOptions = useMemo(() => {
    if (!slackChannels || slackChannels.length === 0) {
      return [];
    }
    return slackChannels.map(channel => ({
      label: `#${channel.config?.name || channel.id}`,
      value: String(channel.id),
    }));
  }, [slackChannels]);

  const validateEmail = useCallback((email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  }, []);

  return (
    <Stack direction="column" gap="2xl" className="al-p-6">
      {/* Step 1: Subscription Type - Completed */}
      <StepCard
        title="Subscription Type"
        status="completed"
        onEdit={() => onGoToStep(WizardStepEnum.SUBSCRIPTION_TYPE)}
      />

      {/* Step 2: Customize - Completed */}
      <StepCard
        title="Customize Your Subscription"
        status="completed"
        onEdit={() => onGoToStep(WizardStepEnum.CUSTOMIZE)}
      />

      <Stack direction="column" gap="lg" className="al-border al-rounded-lg al-p-4">
        {/* Select your Destination - Active */}
        <div>
          <Typography variant="body" weight="medium" className="al-text-gray-900 al-mb-4">
            Select your Destination
          </Typography>

          <div className="al-space-y-6">
            {/* Send alerts via */}
            <div className="al-min-w-full">
              <Label className="al-text-sm al-font-medium al-mb-3 al-block">
                Send alerts via <span className="al-text-red-600">*</span>
              </Label>
              <Controller
                name="channels"
                control={control}
                render={({ field }) => (
                  <Combobox
                    options={[
                      { label: "Email", value: ChannelType.EMAIL },
                      { label: "Slack", value: ChannelType.SLACK },
                    ]}
                    value={field.value}
                    onChange={value => field.onChange(value as ChannelType[])}
                    placeholder="Select channels"
                    multiSelect={true}
                    buttonProps={{ className: "al-w-full" }}
                  />
                )}
              />
              {errors.channels && (
                <Typography
                  variant="body" size="xs"
                  className="al-text-red-600 al-text-xs al-mt-1 al-block"
                >
                  {errors.channels.message}
                </Typography>
              )}
            </div>

            {/* Email recipients */}
            <Controller
              name="channels"
              control={control}
              render={({ field: channelsField }) => (
                <>
                  {channelsField.value.includes(ChannelType.EMAIL) && (
                    <div>
                      <Label className="al-text-sm al-font-medium al-mb-2 al-block">
                        Email recipients <span className="al-text-red-600">*</span>
                      </Label>
                      <Controller
                        name="email_recipients"
                        control={control}
                        render={({ field }) => {
                          // Ensure field.value is always an array
                          const emails = Array.isArray(field.value) ? field.value : [];

                          return (
                            // @ts-ignore - TagsInput types don't include children but component accepts it
                            <TagsInput
                              value={emails}
                              onValueChange={(next: string[]) => field.onChange(next)}
                              className="al-w-full"
                            >
                              <TagsInputList className="al-w-full">
                                {emails.map(email => {
                                  const isValid = validateEmail(email);
                                  return (
                                    <TagsInputItem
                                      key={email}
                                      value={email}
                                      className={
                                        isValid
                                          ? "al-bg-blue-100 al-text-blue-800 al-border-blue-200"
                                          : "al-bg-red-100 al-text-red-800 al-border-red-200"
                                      }
                                    >
                                      {email}
                                    </TagsInputItem>
                                  );
                                })}
                                <TagsInputInput
                                  placeholder={emails.length === 0 ? "Enter email addresses" : ""}
                                />
                              </TagsInputList>
                            </TagsInput>
                          );
                        }}
                      />
                      <Typography variant="body" size="xs" className="al-text-gray-500 al-mt-1 al-block">
                        Press Enter or comma to add emails
                      </Typography>
                      {errors.email_recipients && (
                        <Typography
                          variant="body" size="xs"
                          className="al-text-red-600 al-text-xs al-mt-1 al-block"
                        >
                          {errors.email_recipients.message}
                        </Typography>
                      )}
                    </div>
                  )}
                </>
              )}
            />

            {/* Slack channels */}
            <Controller
              name="channels"
              control={control}
              render={({ field: channelsField }) => (
                <>
                  {channelsField.value.includes(ChannelType.SLACK) && (
                    <div>
                      <Label className="al-text-sm al-font-medium al-mb-2 al-block">
                        Slack Channels <span className="al-text-red-600">*</span>
                      </Label>
                      <Controller
                        name="slack_channels"
                        control={control}
                        render={({ field }) => (
                          <Combobox
                            options={slackChannelOptions}
                            value={field.value}
                            onChange={value => field.onChange(value as string[])}
                            placeholder={
                              isLoadingChannels ? "Loading channels..." : "Select Slack channels"
                            }
                            searchPlaceholder="Search channels..."
                            multiSelect={true}
                            buttonProps={{ className: "al-w-full", disabled: isLoadingChannels }}
                          />
                        )}
                      />
                      {errors.slack_channels && (
                        <Typography
                          variant="body" size="xs"
                          className="al-text-red-600 al-text-xs al-mt-1 al-block"
                        >
                          {errors.slack_channels.message}
                        </Typography>
                      )}
                      {!isLoadingChannels && slackChannelOptions.length === 0 && (
                        <Typography
                          variant="body" size="xs"
                          className="al-text-amber-600 al-text-xs al-mt-1 al-block"
                        >
                          No Slack channels configured. Add channels in Settings → Notifications.
                        </Typography>
                      )}
                    </div>
                  )}
                </>
              )}
            />
          </div>
        </div>
      </Stack>
    </Stack>
  );
};

export default DestinationStep;
